import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Save, Clock, Shield } from "lucide-react";
import { Switch } from "@/components/ui/switch";

const DAYS = [
  { key: "mon", label: "Monday" }, { key: "tue", label: "Tuesday" }, { key: "wed", label: "Wednesday" },
  { key: "thu", label: "Thursday" }, { key: "fri", label: "Friday" }, { key: "sat", label: "Saturday" }, { key: "sun", label: "Sunday" },
];

export default function Availability() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/settings").then((r) => setSettings(r.data)); }, []);

  const update = (patch) => setSettings((s) => ({ ...s, ...patch }));

  const addRange = (day) => {
    const wh = { ...settings.working_hours };
    wh[day] = [...(wh[day] || []), { start: "09:00", end: "12:00" }];
    update({ working_hours: wh });
  };
  const removeRange = (day, i) => {
    const wh = { ...settings.working_hours };
    wh[day] = wh[day].filter((_, idx) => idx !== i);
    update({ working_hours: wh });
  };
  const setRange = (day, i, field, val) => {
    const wh = { ...settings.working_hours };
    wh[day] = wh[day].map((r, idx) => (idx === i ? { ...r, [field]: val } : r));
    update({ working_hours: wh });
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put("/settings", settings);
      setSettings(res.data);
      toast.success("Availability saved");
    } catch (e) {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return null;

  return (
    <div className="space-y-8" data-testid="availability-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Best-hours availability</h1>
          <p className="mt-1 text-slate-600">Only the hours you set here are ever offered to bookers.</p>
        </div>
        <button onClick={save} disabled={saving} className="brand-btn" data-testid="save-availability-btn">
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save changes"}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
          <div className="flex items-center gap-2 text-blue-600 mb-4"><Shield className="w-5 h-5" /><h2 className="text-lg font-semibold text-slate-900">Caps & buffers</h2></div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">Max meetings per day</label>
          <input type="number" min="1" value={settings.daily_cap} data-testid="daily-cap-input"
            onChange={(e) => update({ daily_cap: parseInt(e.target.value || "0", 10) })}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" />
          <p className="mt-1.5 text-xs text-slate-500">Once reached, your calendar auto-locks to Unbookable.</p>

          <label className="block text-sm font-medium text-slate-700 mb-1.5 mt-5">Buffer between meetings</label>
          <select value={settings.buffer_minutes} data-testid="buffer-select"
            onChange={(e) => update({ buffer_minutes: parseInt(e.target.value, 10) })}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none">
            {[0, 5, 10, 15, 30].map((m) => <option key={m} value={m}>{m} minutes</option>)}
          </select>

          <label className="block text-sm font-medium text-slate-700 mb-1.5 mt-5">Timezone label</label>
          <input type="text" value={settings.timezone} data-testid="timezone-input"
            onChange={(e) => update({ timezone: e.target.value })}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none" />

          <div className="mt-5 flex items-center justify-between rounded-lg bg-slate-50 border border-slate-200 px-3 py-2.5">
            <span className="text-sm font-medium text-slate-700">Deep Work lock</span>
            <Switch checked={settings.manual_unbookable} data-testid="availability-unbookable-toggle"
              onCheckedChange={(v) => update({ manual_unbookable: v })} />
          </div>
        </div>

        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl shadow-sm p-6">
          <div className="flex items-center gap-2 text-blue-600 mb-4"><Clock className="w-5 h-5" /><h2 className="text-lg font-semibold text-slate-900">Weekly working hours</h2></div>
          <div className="space-y-3">
            {DAYS.map((d) => {
              const ranges = settings.working_hours[d.key] || [];
              return (
                <div key={d.key} className="flex items-start gap-4 py-2 border-b border-slate-100 last:border-0" data-testid={`day-row-${d.key}`}>
                  <div className="w-24 pt-2 text-sm font-semibold text-slate-800 shrink-0">{d.label}</div>
                  <div className="flex-1 space-y-2">
                    {ranges.length === 0 && <p className="text-sm text-slate-400 pt-2">Unavailable</p>}
                    {ranges.map((r, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <input type="time" value={r.start} data-testid={`${d.key}-start-${i}`}
                          onChange={(e) => setRange(d.key, i, "start", e.target.value)}
                          className="rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
                        <span className="text-slate-400">–</span>
                        <input type="time" value={r.end} data-testid={`${d.key}-end-${i}`}
                          onChange={(e) => setRange(d.key, i, "end", e.target.value)}
                          className="rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
                        <button onClick={() => removeRange(d.key, i)} data-testid={`remove-${d.key}-${i}`}
                          className="p-1.5 text-slate-400 hover:text-red-600 transition-colors"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    ))}
                    <button onClick={() => addRange(d.key)} data-testid={`add-range-${d.key}`}
                      className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700">
                      <Plus className="w-4 h-4" /> Add slot
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
